import type { Route } from 'next';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  CalendarDays,
  NotebookPen,
  Plus,
  Settings as SettingsIcon,
} from 'lucide-react';

export interface CommandDescriptor {
  id: string;
  /** Visible name in the palette. */
  title: string;
  /** Section label — groups related commands under a quiet header. */
  group: 'Go to' | 'Create';
  icon: LucideIcon;
  /** When present, the chord that triggers this command outside the
   *  palette (e.g. "g s"). Displayed on the right of the palette row
   *  as a kbd hint. */
  shortcut?: string;
  /** What to do when selected. Receives utilities so commands can
   *  navigate without hard-coding next/navigation. */
  run: (utils: CommandRunUtils) => void;
}

export interface CommandRunUtils {
  /** Pushes the given path with the typed-routes cast applied. */
  navigate: (path: string) => void;
  /** Opens the palette in a "creating a note" state — the create
   *  side-effect actually runs via the command's own logic; this is
   *  here for commands that orchestrate multi-step flows. */
  closePalette: () => void;
  /** Mutation: kick off a new-note create. The provider injects this
   *  so the command list doesn't need to import the hook directly. */
  createNote: () => void;
}

// Static registry. New commands go here; the palette + chord handler
// both read from this same list so they stay in sync.
export const COMMANDS: CommandDescriptor[] = [
  {
    id: 'go-schedule',
    title: 'Go to Schedule',
    group: 'Go to',
    icon: CalendarDays,
    shortcut: 'g s',
    run: ({ navigate }) => navigate('/'),
  },
  {
    id: 'go-notes',
    title: 'Go to Notes',
    group: 'Go to',
    icon: NotebookPen,
    shortcut: 'g n',
    run: ({ navigate }) => navigate('/notes'),
  },
  {
    id: 'go-analytics',
    title: 'Go to Analytics',
    group: 'Go to',
    icon: BarChart3,
    shortcut: 'g a',
    run: ({ navigate }) => navigate('/analytics'),
  },
  {
    id: 'go-settings',
    title: 'Go to Settings',
    group: 'Go to',
    icon: SettingsIcon,
    shortcut: 'g ,',
    run: ({ navigate }) => navigate('/settings'),
  },
  {
    id: 'create-note',
    title: 'New note',
    group: 'Create',
    icon: Plus,
    run: ({ createNote, closePalette }) => {
      createNote();
      closePalette();
    },
  },
];

/** Filters by case-insensitive substring against title + group. */
export function filterCommands(query: string): CommandDescriptor[] {
  const q = query.trim().toLowerCase();
  if (!q) return COMMANDS;
  return COMMANDS.filter(
    (c) =>
      c.title.toLowerCase().includes(q) || c.group.toLowerCase().includes(q),
  );
}

// Reusable for the typed-routes cast on dynamic navigation.
export function asRoute(path: string): Route {
  return path as Route;
}
